import { Dispatch } from 'redux';

import { EMPTY_VALUE } from '@constants/common.constants';
import { DATE_FORMATS } from '@constants/common/dateTimeValues';
import { configs } from '@constants/configs/configs';
import { formatDate } from '@utils/dates/date.utils';
import { convertLocalToServerTimestamp } from '@utils/dates/dateMethods';
import {
    ANY,
    AvDecision,
    booleanTypes,
    CompleteTicketAction,
    DEFAULT_PAYLOAD,
    permissions,
    taskStatusTypes,
    TICKET_LIST_PAGE_INFO,
    TICKET_LIST_SEARCH_FROM_REPORTS,
    TICKET_LIST_SEARCH_VARS,
    TicketApiType,
    ticketStatusTypes,
    validateTypes,
} from './ticketlist.constants';
import { IFilterOptions, IRefreshTicketList } from './ticketlist.interface';

export const statusChanged = (
    ticket: any,
    isArchivedAllowed: boolean,
    isAssignAllowed: boolean,
    isSimplifiedView: boolean,
) => {
    const updatedTicket = { ...ticket };

    updatedTicket.allowView = true;
    updatedTicket.allowEdit = false;
    updatedTicket.allowCancel = false;
    updatedTicket.allowMarkAsComplete = false;
    updatedTicket.allowClaim = false;
    updatedTicket.allowReassign = false;
    updatedTicket.allowAssign = false;
    updatedTicket.allowReopen = false;
    updatedTicket.allowRestart = false;
    updatedTicket.allowArchive = false;

    updatedTicket.isCheckboxDisabled =
        updatedTicket.status == ticketStatusTypes.RESOLVED || updatedTicket.status == ticketStatusTypes.CANCELED;

    if (!isSimplifiedView) {
        if (
            updatedTicket.status === ticketStatusTypes.COMPLETED ||
            updatedTicket.status === ticketStatusTypes.CANCELED ||
            updatedTicket.status === ticketStatusTypes.RESOLVED
        ) {
            updatedTicket.allowEdit = false;
            updatedTicket.allowCancel = false;
            updatedTicket.allowAssign = false;
        } else {
            updatedTicket.allowEdit = true;
            updatedTicket.allowCancel = true;
            if (updatedTicket.taskStatus === taskStatusTypes.CLAIMED) {
                updatedTicket.allowReassign = true;
            } else if (updatedTicket.taskStatus === taskStatusTypes.UNCLAIMED) {
                updatedTicket.allowAssign = true;
            }
        }
    }

    if (
        updatedTicket.status !== ticketStatusTypes.COMPLETED &&
        updatedTicket.status !== ticketStatusTypes.RESOLVED &&
        updatedTicket.taskStatus !== taskStatusTypes.CANCELED
    ) {
        if (updatedTicket.taskStatus === taskStatusTypes.CLAIMED) {
            updatedTicket.allowMarkAsComplete = true;
        }
        if (updatedTicket.taskStatus === taskStatusTypes.UNCLAIMED) {
            updatedTicket.allowClaim = true;
        }
        if (updatedTicket.taskStatus === taskStatusTypes.OPEN) {
            updatedTicket.allowMarkAsComplete = true;
            updatedTicket.allowReassign = true;
        }
        if (
            updatedTicket.taskStatus === taskStatusTypes.REOPENED ||
            updatedTicket.taskStatus === taskStatusTypes.RESTARTED
        ) {
            updatedTicket.allowClaim = true;
            updatedTicket.allowAssign = true;
        }
    }

    if (updatedTicket.status === ticketStatusTypes.CANCELED || updatedTicket.status === ticketStatusTypes.RESOLVED) {
        if (updatedTicket.archiveFlag === 'Y') {
            updatedTicket.allowReopen = false;
            updatedTicket.allowRestart = false;
        } else {
            updatedTicket.allowReopen = true;
            updatedTicket.allowRestart = true;
        }
        if (updatedTicket.workflowpermission === permissions.TKM_WORKFLOW_VERIFY) {
            updatedTicket.allowReopen = false;
        }
    }

    if (isArchivedAllowed) {
        if (
            (updatedTicket.status !== ticketStatusTypes.COMPLETED &&
                updatedTicket.status !== ticketStatusTypes.RESOLVED &&
                updatedTicket.taskStatus !== taskStatusTypes.CANCELED) ||
            updatedTicket.archiveFlag === validateTypes.Y
        ) {
            updatedTicket.allowArchive = false;
        } else {
            updatedTicket.allowArchive = true;
        }
    } else {
        updatedTicket.allowArchive = false;
    }

    if (updatedTicket.allowAssign && !isAssignAllowed) {
        updatedTicket.allowAssign = false;
    }
    if (updatedTicket.status === ticketStatusTypes.CANCELED) {
        updatedTicket.allowClaim = false;
    }

    return updatedTicket;
};
export const processTicketData = (
    tickets: any,
    ticketController: any,
    ocrVerificationTicketIdList: Array<string | number> = [],
) => {
    const { isSimplifiedView, isArchivedAllowed, isAssignAllowed, pageSize } = ticketController;
    const ocrTicketIds = new Set((ocrVerificationTicketIdList || []).map(id => String(id)));

    const updatedTickets = tickets?.map(ticket => {
        const updatedTicket = statusChanged(ticket, isArchivedAllowed, isAssignAllowed, isSimplifiedView);
        updatedTicket.isOcrFlow = ocrTicketIds.has(String(ticket.id));
        return updatedTicket;
    });

    return updatedTickets;
};

export const isRejectResult = (result?: string): boolean => String(result || '').toLowerCase() === AvDecision.REJECTED;

export const isErrorResult = (result?: string): boolean => String(result || '').toLowerCase() === AvDecision.ERROR;

export const formatAvCommentLines = (text?: string | null): string[] => {
    if (!text?.trim()) {
        return [EMPTY_VALUE];
    }
    return text
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .split(/\r?\n/);
};

export interface IBuildCompleteTaskPayloadParams {
    activitiTaskId: string | number;
    processInstanceId: string | number;
    taskId: string | number;
    ticketId: string | number;
    validateComments: string | boolean;
    taskStatus: string;
    taskRejectionReason?: string;
    internalComment?: string;
    externalComment?: string;
    extraFields?: Record<string, string | number | undefined>;
}

/** Builds the complete-user-task POST body shared by verify-complete and AVS manual review. */
export const buildCompleteTaskPayload = ({
    activitiTaskId,
    processInstanceId,
    taskId,
    ticketId,
    validateComments,
    taskStatus,
    taskRejectionReason = '',
    internalComment = '',
    externalComment = '',
    extraFields = {},
}: IBuildCompleteTaskPayloadParams) => ({
    taskObject: JSON.stringify({
        ActivitiTaskId: activitiTaskId,
        ProcessInstanceId: processInstanceId,
        TaskId: taskId,
        TicketId: ticketId,
        ValidateComments: validateComments,
        Action: CompleteTicketAction.COMPLETE,
        formPropertiesMap: {
            taskStatus,
            taskRejectionReason: taskRejectionReason || '',
        },
    }),
    comments: internalComment,
    commentLength: internalComment?.length,
    publicComments: externalComment,
    csrftoken: window.csrf_token,
    ...extraFields,
});

export const handleChange = (name: string, value: string | string[] | number | number[], setValue) => {
    setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
    });
};

export const handleFieldChange = (name: string, value: string | string[] | number | number[], setValue) => {
    handleChange(name, value, setValue);
};

export const formatFilterValue = (value: string): string => (value === ANY ? '' : value);

export const getItemFromSessionStorage = (key: string) => {
    const item = sessionStorage.getItem(key);
    if (item) {
        return JSON.parse(item);
    }
    return {};
};

export const setItemToSessionStorage = (key: string, value: any) => {
    sessionStorage.setItem(key, JSON.stringify(value));
};

export const getTicketsSessionData = () => {
    const searchCriteria = getItemFromSessionStorage(TICKET_LIST_SEARCH_VARS);
    return searchCriteria;
};

export interface TimezoneInfo {
    timezoneOffsetMs: number;
    dstSavingsMs: number;
}

const isValidDate = (value: unknown): value is Date | string =>
    (value instanceof Date || typeof value === 'string') &&
    !Number.isNaN(new Date(value as string | number).getTime());

const formatDateForServer = (
    dateValue: unknown,
    timezoneInfo?: TimezoneInfo,
): string => {
    if (!dateValue || !isValidDate(dateValue)) return '';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (timezoneInfo) {
        return convertLocalToServerTimestamp(
            date,
            timezoneInfo.timezoneOffsetMs,
            timezoneInfo.dstSavingsMs,
            DATE_FORMATS.MM_DD_YYYY,
        );
    }
    return formatDate(date, DATE_FORMATS.MM_DD_YYYY);
};

export const setTicketsSessionData = (
    data: any,
    isDefaultSearch: boolean = false,
    timezoneInfo?: TimezoneInfo,
) => {
    const searchCriteria = getTicketsSessionData();

    let ticketData: any = {
        userticketQueues: data.workgroup || '',
        filterticketStatus: data.ticketStatus || '',
        userFilter: data.ticketAssignment || '',
        watchStatusBasic: data.watchStatusBasic || '',
    };
    if (!isDefaultSearch) {
        ticketData = {
            ticketSubject: data.ticketSubject || '',
            ticketComments: data.ticketComments || '',
            requestedByName: data.requestedByName || '',
            requestedBy: data.requestedBy || '',
            ticketQueues: data.ticketQueues?.join(',') || '',
            ticketPriority: data.ticketPriority?.join(',') || '',
            watchStatusAdvanced: data.watchStatusAdvanced?.join() || '',
            confidentialTicketsFilter: data.confidentialTicketsFilter?.join() || '',
            assignee: data.assignee || '',
            assigneeId: data.assigneeId || '',
            tagId: data.tagId || '',
            requestedBytagId: data.requestedBytagId || '',
            ticketNumber: data.ticketNumber || '',
            submittedBefore: formatDateForServer(data.submittedBefore, timezoneInfo),
            submittedAfter: formatDateForServer(data.submittedAfter, timezoneInfo),
            ticketStatus: data.ticketStatus?.join(',') || '',
            assignedToMe: false,
            unClaimedTicket: false,
            requester: data.requester || '',
            roleId: data.roleId || '',
            archiveFlagValue: false,
            customFieldSelect: data.customFieldSelect || -1,
            customFieldSelectVal:
                data.customFieldSelectVal && isValidDate(data.customFieldSelectVal)
                    ? formatDateForServer(data.customFieldSelectVal, timezoneInfo)
                    : data.customFieldSelectVal,
            ticketCategorySelect: data.ticketCategorySelect || -1,
            ticketType: data.ticketType?.join(',') || '',
            workGroupBytagId: data.workGroupBytagId || '',
            userRole: data.userRole,
            customFieldSelectValOptionId: data.customFieldSelectValOptionId || '',
        };
    }

    setItemToSessionStorage(TICKET_LIST_SEARCH_VARS, {
        ...searchCriteria,
        ...ticketData,
    });

    return ticketData;
};

export const createTicketPayload = (isFilteredCall: boolean = false, isDefaultCall: boolean = false) => {
    const searchCriteria = getTicketsSessionData();
    const { currentPage = 1, sortField = '', sortOrder = '' } = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};

    let payload = { ...DEFAULT_PAYLOAD };
    if (isFilteredCall) {
        payload = {
            ...payload,
            filterticketStatus: formatFilterValue(searchCriteria?.filterticketStatus) || '',
            userticketQueues: formatFilterValue(searchCriteria?.userticketQueues) || '',
            userFilter: searchCriteria.userFilter || '',
            watchStatusBasic: searchCriteria.watchStatusBasic || '',
        };
        if (window.location.search === '?dueDateReport=true') {
            const reportTicketDashboard = getItemFromSessionStorage(TICKET_LIST_SEARCH_FROM_REPORTS);
            payload = {
                ...reportTicketDashboard,
            };
        }
    } else if (isDefaultCall) {
        payload = {
            ...payload,
            filterticketStatus: 'Open',
            userticketQueues: '',
            userFilter: 'assignedToMeFilter',
            watchStatusBasic: 'all',
        };
    } else {
        payload = {
            ...payload,
            ticketSubject: searchCriteria.ticketSubject || '',
            ticketComments: searchCriteria.ticketComments || '',
            requestedByName: searchCriteria.requestedByName || '',
            requestedBy: searchCriteria.requestedBy || '',
            ticketQueues: searchCriteria.ticketQueues || '',
            ticketPriority: searchCriteria.ticketPriority || '',
            watchStatusAdvanced: searchCriteria.watchStatusAdvanced || '',
            watchStatusBasic: searchCriteria.watchStatusBasic || '',
            assignee: searchCriteria.assignee || '',
            assigneeId: searchCriteria.assigneeId || '',
            tagId: searchCriteria.tagId || '',
            requestedBytagId: searchCriteria.requestedBytagId || '',
            ticketNumber: searchCriteria.ticketNumber || '',
            submittedBefore: searchCriteria.submittedBefore || '',
            submittedAfter: searchCriteria.submittedAfter || '',
            ticketStatus: searchCriteria.ticketStatus || '',
            assignedToMe: false,
            unClaimedTicket: false,
            requester: searchCriteria.requester || '',
            roleId: searchCriteria.roleId || '',
            archiveFlagValue: false,
            customFieldSelect: searchCriteria.customFieldSelect || -1,
            customFieldSelectVal: searchCriteria.customFieldSelectVal || '',
            ticketCategorySelect: searchCriteria.ticketCategorySelect || -1,
            ticketType: searchCriteria.ticketType || '',
            workGroupBytagId: searchCriteria.workGroupBytagId || '',
            userRole: searchCriteria.userRole || '',
            confidentialTicketsFilter: searchCriteria?.confidentialTicketsFilter || '',
        };
    }
    return {
        ...payload,
        pageNumber: currentPage,
        sortBy: sortField,
        sortOrder,
        csrftoken: window.csrf_token,
    };
};

export const parseQueryString = () => {
    const getQuery = window.location.search;
    const queryString = String(getQuery).replace(/\?/gi, '');

    const queryArray = queryString.split('&');

    const retObj: Record<string, string | undefined> = {
        period: undefined,
        ticketPriority: undefined,
    };

    queryArray.forEach(obj => {
        const valSplit = String(obj).split('=');
        if (valSplit[0]) {
            retObj[valSplit[0]] = decodeURIComponent(valSplit[1] || '');
        }
    });

    if (retObj.ticketPriority !== undefined) {
        retObj.ticketPriority = String(retObj.ticketPriority).replace('[', '').replace(']', '');
    }

    if (retObj.fromAgeingReports === booleanTypes.TRUE) {
        retObj.ticketStatus = `${ticketStatusTypes.OPEN},${ticketStatusTypes.UNCLAIMED}`;
    }
    if (retObj.fromPendingReport === booleanTypes.TRUE) {
        retObj.ticketStatus = ticketStatusTypes.OPEN;
    }

    return retObj;
};

export const getPath = (fileName: string) => {
    return configs.ENV.DEVELOPMENT
        ? `../../../../assets/images/${fileName}`
        : configs.endpoints.IMAGES.processReplacements({
            relativePath: '/resources/images',
            fileName,
        });
};

export const getViewDetailUrl = (ticketId: string) => {
    const url = new URL(
        configs.endpoints.TICKET_MGMT_TICKET_DETAIL.processReplacements({ encTicketId: ticketId }),
        window.location.origin,
    );
    return url.toString();
};

export const highlightFields = (data: any): IFilterOptions => {
    return Object.fromEntries(
        Object.entries(data).map(([fieldName, fieldValue]) => {
            if (Array.isArray(fieldValue)) {
                return [fieldName, fieldValue.length > 0];
            }
            const isValid = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && fieldValue !== -1;
            return [fieldName, isValid];
        }),
    ) as IFilterOptions;
};

export const updateTicketList = (
    dispatch: Dispatch<any>,
    ticketList: any[],
    key: string,
    value: any,
    updatedData: { taskStatus: string; status: string },
) => {
    const index = ticketList.findIndex(ticket => ticket[key] === value);
    if (index !== -1) {
        const updatedTickets = [...ticketList];
        updatedTickets[index] = {
            ...updatedTickets[index],
            ...updatedData,
        };
        dispatch({ type: 'UPDATE_TICKETS', payload: updatedTickets });
    }
};

export const refreshTicketList = async (props: IRefreshTicketList) => {
    const { dispatch, listApiType, defaultListAPI, listAPI, filteredListAPI } = props;
    if (listApiType === TicketApiType.DEFAULTLIST) {
        const payload = createTicketPayload(false, true);
        defaultListAPI(dispatch, payload);
    } else if (listApiType === TicketApiType.FILTEREDLIST) {
        const payload = createTicketPayload(true);
        filteredListAPI(dispatch, payload);
    } else {
        const payload = createTicketPayload();
        listAPI(dispatch, payload);
    }
};
